import { Lock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/staff/ui";

const CANT_GIVE = "You don't have this permission yourself, so you can't give it or take it away.";

// Starting ticks for a role, limited to what this person may hand out (the owner may hand out everything).
export const startingTicks = (catalog, role) => (catalog.defaults[role] || []).filter((p) => catalog.mine.includes(p));

export const permLabels = (catalog) => Object.fromEntries(catalog.groups.flatMap((g) => g.items.map((i) => [i.key, i.label])));

function Item({ item, checked, disabled, why, onToggle, testid }) {
  return (
    <label className={`flex items-start gap-3 rounded-xl px-3 py-2.5 ${disabled ? "opacity-55 cursor-not-allowed" : "cursor-pointer hover:bg-white/[0.03]"}`} data-testid={testid}>
      <Checkbox checked={checked} disabled={disabled} onCheckedChange={onToggle} aria-describedby={`${testid}-help`}
        className="mt-0.5 h-[18px] w-[18px] rounded-[5px] border-[#2B3B5C] data-[state=checked]:bg-sky-400 data-[state=checked]:border-sky-400 data-[state=checked]:text-white"
        data-testid={`${testid}-box`} />
      <span className="min-w-0">
        <span className="block text-sm text-neutral-100">{item.label}</span>
        <span id={`${testid}-help`} className="block text-xs text-neutral-500 mt-0.5">{why || item.help}</span>
      </span>
    </label>
  );
}

// The grouped tick-box list: Students, Money, Running the app, and the owner-only items (never tickable).
export function PermissionPicker({ catalog, value, onChange, readOnly, testid = "perm" }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((p) => p !== k) : [...value, k]);
  return (
    <div className="space-y-5" data-testid={testid}>
      {catalog.groups.map((g) => (
        <fieldset key={g.key} data-testid={`${testid}-group-${g.key}`}>
          <legend className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">{g.label}</legend>
          <div className="grid md:grid-cols-2 gap-x-3">
            {g.items.map((i) => {
              const cantGive = !catalog.mine.includes(i.key);
              return (
                <Item key={i.key} item={i} checked={value.includes(i.key)} disabled={readOnly || cantGive} why={!readOnly && cantGive ? CANT_GIVE : ""}
                  onToggle={() => toggle(i.key)} testid={`${testid}-${i.key.replace(".", "-")}`} />
              );
            })}
          </div>
        </fieldset>
      ))}
      <fieldset data-testid={`${testid}-group-owner`}>
        <legend className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5 flex items-center gap-2">Owner only <Lock className="h-3 w-3" aria-hidden="true" /></legend>
        {catalog.owner_only.map((i) => (
          <div key={i.key} className="flex items-start gap-3 rounded-xl px-3 py-2.5 opacity-70" data-testid={`${testid}-owner-${i.key.replace(".", "-")}`}>
            <Lock className="h-4 w-4 mt-0.5 text-amber-200 shrink-0" aria-hidden="true" />
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-2 text-sm text-neutral-100">{i.label}<Badge tone="amber">Owner only</Badge></span>
              <span className="block text-xs text-neutral-500 mt-0.5">{i.help} It can't be given to anyone.</span>
            </span>
          </div>
        ))}
      </fieldset>
    </div>
  );
}
